/**
 * Motion of "Buscar en esta conversación" (SRE-3328), read from the DS motion
 * tokens (@leadsales/design-system/tokens, src/tokens/_motion.scss) at run
 * time, so a DS change of a duration or a curve reaches the bar without an
 * edit here.
 *
 * It runs on the Web Animations API, not on CSS transitions: the FLIP below
 * needs measured start values, and the DS reduced-motion net
 * (indigo-ds-tokens.css) cuts every CSS transition to 0.01 ms, which would also
 * cut the opacity-only fade that guidelines/motion.md keeps under
 * `prefers-reduced-motion`. WAAPI is not reached by that net, so, as the
 * guideline asks of `element.animate()`, the media query is checked here.
 *
 * DES-TBD: the bar growing out of the magnifier to the chat's width (FLIP of a
 * scaleX from the button, the icon travelling into the field) is an exception
 * to the DS `reveal` pattern (which scales from 0.95 or more), approved by
 * Ariel Contreras (Product) on 2026-10-01. Everything else follows
 * guidelines/motion.md. Replace DES-TBD with the ticket that registers it.
 */

type tTiming = { duration: number; easing: string };

export type tChatSearchMotionTokens = {
  /** --ls-duration-lg + --ls-ease-standard: the field grows (300 ms). */
  grow: tTiming;
  /** --ls-duration-md + --ls-ease-exit: the field goes back (200 ms). */
  shrink: tTiming;
  /** --ls-ease-enter: placeholder, counter and "Cerrar" coming in. */
  easeEnter: string;
  /** --ls-motion-reveal-enter: ChatMenu's controls coming back (150 ms). */
  revealEnter: tTiming;
  /** --ls-spacing-2: how far the content travels as it fades in (8 px). */
  contentShift: string;
};

/**
 * The content (placeholder, counter, "Cerrar") waits for the magnifier to land:
 * it enters over the last fifth of the grow, 240 to 300 ms of 300.
 */
export const CONTENT_ENTER_FROM = 0.8;

/**
 * On "Cerrar", text, counter and "Cerrar" go out in 40 ms before the field
 * shrinks. Product's figure; the DS has no token this short (--ls-duration-xs
 * is 100 ms).
 */
export const CONTENT_EXIT_MS = 40;

function readProperty(styles: CSSStyleDeclaration, name: string): string {
  return styles.getPropertyValue(name).trim();
}

/** "300ms" or "0.3s" in milliseconds, or NaN. */
function toMs(value: string): number {
  const match = /^(-?[\d.]+)(ms|s)$/.exec(value);

  if (!match) return Number.NaN;

  return Number(match[1]) * (match[2] === 's' ? 1000 : 1);
}

/** An intent token, `<duration> <easing>`, split in two. */
function readIntent(value: string): tTiming {
  const [duration, ...easing] = value.split(/\s+/);

  return { duration: toMs(duration ?? ''), easing: easing.join(' ') };
}

/**
 * Reads the DS motion tokens the bar uses from the document.
 *
 * @returns The tokens, or null when any of them is missing (the DS tokens not
 * loaded): with no token there is no motion, and the bar opens and closes at
 * once.
 */
export function readChatSearchMotionTokens(
  root: Element = document.documentElement,
): tChatSearchMotionTokens | null {
  const styles = getComputedStyle(root);
  const lg = toMs(readProperty(styles, '--ls-duration-lg'));
  const md = toMs(readProperty(styles, '--ls-duration-md'));
  const easeStandard = readProperty(styles, '--ls-ease-standard');
  const easeEnter = readProperty(styles, '--ls-ease-enter');
  const easeExit = readProperty(styles, '--ls-ease-exit');
  const revealEnter = readIntent(
    readProperty(styles, '--ls-motion-reveal-enter'),
  );
  const contentShift = readProperty(styles, '--ls-spacing-2');

  const complete =
    Number.isFinite(lg) &&
    Number.isFinite(md) &&
    Number.isFinite(revealEnter.duration) &&
    [easeStandard, easeEnter, easeExit, revealEnter.easing, contentShift].every(
      Boolean,
    );

  if (!complete) return null;

  return {
    grow: { duration: lg, easing: easeStandard },
    shrink: { duration: md, easing: easeExit },
    easeEnter,
    revealEnter,
    contentShift,
  };
}

/** Whether the user asked the system for reduced motion. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;

  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Whether this browser runs the Web Animations API (jsdom does not). */
export function canAnimate(element: Element | null): element is Element {
  return Boolean(element) && typeof element?.animate === 'function';
}

/**
 * ChatMenu's controls coming back once the bar has gone back into the
 * magnifier: a `reveal` enter, opacity only, which reduced motion keeps.
 *
 * @param element - ChatMenu's slot.
 */
export function revealChatMenu(element: HTMLElement | null): void {
  const tokens = readChatSearchMotionTokens();

  if (!tokens || !canAnimate(element)) return;

  element.animate([{ opacity: 0 }, { opacity: 1 }], tokens.revealEnter);
}
